import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  InjectionToken,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  provideTranslateCompiler,
  provideTranslateService,
  TranslateService,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideApollo } from 'apollo-angular';
import { createClient, Client as GqlWsClient } from 'graphql-ws';

import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { it } from 'primelocale/it.json';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { langInterceptor } from './_core/_interceptors/lang.interceptor';
import { InitService } from './_core/_services/init.service';
import { j4g } from './_core/_theme/j4g.theme';
import { routes } from './app.routes';
import { Kind, OperationTypeNode } from 'graphql';
import { HttpLink } from 'apollo-angular/http';
import { FORMLY_CONFIG, provideFormlyCore } from '@ngx-formly/core';
import { AccordionFieldType } from './_core/_formly/_types/accordion.type';
import { AddressFieldType } from './_core/_formly/_types/address.type';
import { ButtonFieldType } from './_core/_formly/_types/button.type';
import { CharacteristicsFieldType } from './_core/_formly/_types/characteristics.type';
import { CheckboxFieldType } from './_core/_formly/_types/checkbox.type';
import { DatepickerFieldType } from './_core/_formly/_types/datepicker.type';
import { DocumentFieldType } from './_core/_formly/_types/document.type';
import { EditorFieldType } from './_core/_formly/_types/editor.type';
import { GeocodeFieldType } from './_core/_formly/_types/geocode.type';
import { MapFieldType } from './_core/_formly/_types/map.type';
import { MappaFieldType } from './_core/_formly/_types/mappa.type';
import { MultiSelectFieldType } from './_core/_formly/_types/multiselect.type';
import { NumberFieldType } from './_core/_formly/_types/number.type';
import { PasswordFieldType } from './_core/_formly/_types/password.type';
import { RadioFieldType } from './_core/_formly/_types/radio.type';
import { RepeaterAccordionFieldType } from './_core/_formly/_types/repeater-accordion.type';
import { RepeaterFieldType } from './_core/_formly/_types/repeater.type';
import { SelectButtonFieldType } from './_core/_formly/_types/select-button.type';
import { SelectFieldType } from './_core/_formly/_types/select.type';
import { StepperFieldType } from './_core/_formly/_types/stepper.type';
import { TabFieldType } from './_core/_formly/_types/tab.type';
import { TemplateFieldType } from './_core/_formly/_types/template.type';
import { TextFieldType } from './_core/_formly/_types/text.type';
import { TextAreaFieldType } from './_core/_formly/_types/textarea.type';
import { ToggleFieldType } from './_core/_formly/_types/toggle.type';
import { CardWrapperFormField } from './_core/_formly/_wrappers/card.wrapper';
import { CheckboxFormlyWrapper } from './_core/_formly/_wrappers/checkbox.wrapper';
import { DrawerWrapperFormField } from './_core/_formly/_wrappers/drawer.wrapper';
import { EditableFormlyWrapper } from './_core/_formly/_wrappers/editable.wrapper';
import { FormlyWrapperFormField } from './_core/_formly/_wrappers/form-field.wrapper';
import { SearchFormlyWrapper } from './_core/_formly/_wrappers/search.wrapper';
import { ContattoValidator } from './_core/_formly/validation/contatto.validation';
import { phoneNumberValidator } from './_core/_formly/validation/phone-number-validation';
import {
  podDuplicatoValidator,
  podValidator,
  podValidatorGSE,
  podValidatorGSEExists,
} from './_core/_formly/validation/pod.validation';
import { referralValidator } from './_core/_formly/validation/referral.validation';
import { TaxIdCodeValidator } from './_core/_formly/validation/tax-id-code-validation';
import { vatNumberValidator } from './_core/_formly/validation/vat-number-validation';
import { EmailValidator } from './_core/_formly/validation/email-validation';
import { withFormlyPrimeNG } from '@ngx-formly/primeng';
import { uniqueReferralValidator } from './_core/_formly/validation/referralvalidation';
import { authInterceptor } from './_core/_interceptors/auth.interceptor';
import { AuthService } from './_core/_services/auth.service';
import { tagInterceptor } from './_core/_interceptors/tag-interceptor';
import { baseUrlInterceptor } from './_core/_interceptors/base-url-interceptor';
import { getTag } from './_core/_utils/get-tag';
import { IbanValidator } from './_core/_formly/validation/iban-validation';
import { codiceOperatoreValidator } from './_core/_formly/validation/codice_operatore-validation';
import { codiceAtecoValidator } from './_core/_formly/validation/codice-ateco-validation';
import { fieldMatchValidator } from './_core/_formly/validation/field-match.validation';
import { provideAmicoRouterListener } from './_core/_interceptors/referral-interceptor';
import { urlValidator } from './_core/_formly/validation/url-validation';
import { MessageFieldType } from './_core/_formly/_types/message.type';
import { CheckboxTreeFieldType } from './_core/_formly/_types/checkbox-tree.type';
import { PicklistFieldType } from './_core/_formly/_types/picklist.type';
import { NgcCookieConsentConfig } from 'ngx-cookieconsent';
import { provideNgcCookieConsent } from 'ngx-cookieconsent';

export const AMICO_LISTENER_INIT = new InjectionToken('AMICO_LISTENER_INIT');

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => inject(InitService).init()),
    provideNgcCookieConsent(environment.cookieConfig),
    MessageService,
    ConfirmationService,
    provideAnimations(),
    providePrimeNG({
      translation: it,
      ripple: true,
      theme: {
        preset: j4g,
        options: {
          darkModeSelector: '.light',
          cssLayer: {
            name: 'primeng',
            order: 'base, components, primeng, utilities',
          },
        },
      },
    }),
    provideTranslateService({
      fallbackLang: 'it',
      loader: provideTranslateHttpLoader({
        prefix: './i18n/',
        suffix: `.json?v=${environment.version}`,
      }),
      compiler: provideTranslateCompiler(TranslateMessageFormatCompiler),
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        langInterceptor,
        tagInterceptor,
        baseUrlInterceptor,
      ]),
    ),
    provideApollo(() => {
      const auth = inject(AuthService);
      const translate = inject(TranslateService);
      const httpLinkHandler = inject(HttpLink).create({
        uri: environment.hasura.url,
      });
      const httpLink = new ApolloLink(
        (operation) => httpLinkHandler.request(operation)!,
      );
      const defaultWsLink = createWsLinkForRole('anonymous', auth, translate);

      const link = ApolloLink.split(
        // Split based on operation type
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === OperationTypeNode.SUBSCRIPTION
          );
        },
        defaultWsLink,
        httpLink,
      );

      return {
        link,
        cache: new InMemoryCache({
          typePolicies: {
            cer: {
              fields: {
                cer_padre: {
                  merge: true,
                },
              },
            },
          },
        }),
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'cache-and-network', // o 'network-only', a seconda della tua esigenza
            errorPolicy: 'ignore',
          },
          query: {
            fetchPolicy: 'network-only', // <-- qui imposti per query come la tua
            errorPolicy: 'all',
          },
        },
      };
    }),
    provideStore(),
    provideEffects(),
    provideRouterStore(),
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: validationConfig,
      deps: [TranslateService],
    },
    provideFormlyCore([
      ...withFormlyPrimeNG(),
      {
        types: [
          { name: 'repeater', component: RepeaterFieldType, wrappers: [] },
          { name: 'stepper', component: StepperFieldType, wrappers: [] },
          { name: 'accordion', component: AccordionFieldType, wrappers: [] },
          {
            name: 'address',
            component: AddressFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'geocode',
            component: GeocodeFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'multiselect',
            component: MultiSelectFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'select',
            component: SelectFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'textarea',
            component: TextAreaFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'editor',
            component: EditorFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'button',
            component: ButtonFieldType,
          },
          {
            name: 'datepicker',
            component: DatepickerFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'text',
            component: TextFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'password',
            component: PasswordFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'toggle',
            component: ToggleFieldType,
            wrappers: ['checkbox'],
          },
          {
            name: 'select-button',
            component: SelectButtonFieldType,
            wrappers: ['checkbox'],
          },
          {
            name: 'radio',
            component: RadioFieldType,
            wrappers: ['checkbox'],
          },
          {
            name: 'checkbox',
            component: CheckboxFieldType,
            wrappers: ['checkbox'],
          },
          {
            name: 'number',
            component: NumberFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'document',
            component: DocumentFieldType,
            wrappers: [],
          },
          {
            name: 'map',
            component: MapFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'mappa',
            component: MappaFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'characteristics',
            component: CharacteristicsFieldType,
            wrappers: ['wrapper'],
          },
          { name: 'tab', component: TabFieldType, wrappers: [] },
          { name: 'template', component: TemplateFieldType, wrappers: [] },
          { name: 'message', component: MessageFieldType, wrappers: [] },
          {
            name: 'repeater-accordion',
            component: RepeaterAccordionFieldType,
            wrappers: [],
          },
          {
            name: 'checkbox-tree',
            component: CheckboxTreeFieldType,
            wrappers: [],
          },
          {
            name: 'picklist',
            component: PicklistFieldType,
            wrappers: ['wrapper'],
          },
        ],
        wrappers: [
          {
            name: 'wrapper',
            component: FormlyWrapperFormField,
          },
          {
            name: 'checkbox',
            component: CheckboxFormlyWrapper,
          },
          {
            name: 'card',
            component: CardWrapperFormField,
          },
          {
            name: 'drawer',
            component: DrawerWrapperFormField,
          },
          {
            name: 'search',
            component: SearchFormlyWrapper,
          },
          {
            name: 'editable',
            component: EditableFormlyWrapper,
          },
        ],
        validators: [
          {
            name: 'url',
            validation: urlValidator,
          },
          {
            name: 'email',
            validation: EmailValidator,
          },
          {
            name: 'contatto',
            validation: ContattoValidator,
          },
          {
            name: 'taxIdCode',
            validation: TaxIdCodeValidator,
          },
          {
            name: 'iban',
            validation: IbanValidator,
          },
          {
            name: 'vatNumber',
            validation: vatNumberValidator,
          },
          {
            name: 'pod',
            validation: podValidator,
          },
          {
            name: 'podGSE',
            validation: podValidatorGSE,
          },
          {
            name: 'podGSEExists',
            validation: podValidatorGSEExists,
          },
          {
            name: 'podDuplicato',
            validation: podDuplicatoValidator,
          },
          {
            name: 'codiceOperatore',
            validation: codiceOperatoreValidator,
          },
          {
            name: 'codiceAteco',
            validation: codiceAtecoValidator,
          },
          {
            name: 'uniqueReferral',
            validation: uniqueReferralValidator,
          },
          {
            name: 'phoneNumber',
            validation: phoneNumberValidator,
          },
          {
            name: 'referral',
            validation: referralValidator,
          },
          {
            name: 'fieldMatch',
            validation: fieldMatchValidator,
          },
        ],
      },
    ]),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    { provide: LOCALE_ID, useValue: 'it-IT' },
  ],
};
export function createWsLinkForRole(
  role: string,
  auth: AuthService,
  translate: TranslateService,
): GraphQLWsLink {
  const lang = translate.getCurrentLang() ?? translate.getFallbackLang();

  return new GraphQLWsLink(
    createClient({
      url: environment.hasura.wss,
      retryAttempts: Infinity,
      shouldRetry: () => true,
      keepAlive: 10000,
      connectionParams: async () => {
        const token = auth.getAccessToken();
        const shouldSendToken = role.toLowerCase() !== 'anonymous';
        let tag = getTag();

        return {
          headers: {
            ...(token && shouldSendToken
              ? { Authorization: `Bearer ${token}` }
              : {}),
            ...(tag ? { 'x-hasura-tag': tag } : {}),
            'x-hasura-lang': translate.getCurrentLang(),
            'x-hasura-role': role,
          },
        };
      },
    }),
  );
}

function validationConfig(translate: TranslateService) {
  return {
    extras: {
      showError(field: any) {
        const control = field?.formControl;
        if (!control) return false;

        const submitted = !!field?.options?.parentForm?.submitted;
        return !!(control.invalid && (control.touched || control.dirty || submitted));
      },
    },
    validationMessages: [
      {
        name: 'percentage',
        message() {
          return translate.stream('formly.validation.percentage');
        },
      },
      {
        name: 'percentage_val',
        message() {
          return translate.stream('formly.validation.percentage_val');
        },
      },
      {
        name: 'percentage_base',
        message() {
          return translate.stream('formly.validation.percentage_base');
        },
      },
      {
        name: 'percentage_eccedenza',
        message() {
          return translate.stream('formly.validation.percentage_eccedenza');
        },
      },
      {
        name: 'required',
        message() {
          return translate.stream('formly.validation.required');
        },
      },
      {
        name: 'iban',
        message() {
          return translate.stream('formly.validation.iban');
        },
      },
      {
        name: 'taxIdCode',
        message() {
          return translate.stream('formly.validation.taxIdCode');
        },
      },
      {
        name: 'contatto',
        message() {
          return translate.stream('formly.validation.contatto');
        },
      },
      {
        name: 'email',
        message() {
          return translate.stream('formly.validation.email');
        },
      },
      {
        name: 'url',
        message() {
          return translate.stream('formly.validation.url');
        },
      },
      {
        name: 'vatNumber',
        message() {
          return translate.stream('formly.validation.vatNumber');
        },
      },
      {
        name: 'pod',
        message() {
          return translate.stream('formly.validation.pod');
        },
      },
      {
        name: 'podGSE',
        message() {
          return translate.stream('formly.validation.podGSE');
        },
      },
      {
        name: 'podGSEExists',
        message() {
          return translate.stream('formly.validation.podGSEExists');
        },
      },
      {
        name: 'podDuplicato',
        message() {
          return translate.stream('formly.validation.podDuplicato');
        },
      },
      {
        name: 'podDuplicatoCheck',
        message() {
          return translate.stream('formly.validation.podDuplicatoCheck');
        },
      },
      {
        name: 'codiceOperatore',
        message() {
          return translate.stream('formly.validation.codiceOperatore');
        },
      },
      {
        name: 'codiceAteco',
        message() {
          return translate.stream('formly.validation.codiceAteco');
        },
      },
      {
        name: 'podGSECheck',
        message() {
          return translate.stream('formly.validation.podGSECheck');
        },
      },
      {
        name: 'phoneNumber',
        message() {
          return translate.stream('formly.validation.phoneNumber');
        },
      },
      {
        name: 'referral',
        message() {
          return translate.stream('formly.validation.referral');
        },
      },
      {
        name: 'uniqueReferral',
        message() {
          return translate.stream('formly.validation.uniqueReferral');
        },
      },
      {
        name: 'fieldMatch',
        message() {
          return translate.stream('formly.validation.fieldMatch');
        },
      },
      {
        name: 'minLength',
        message(field: any) {
          return translate.stream('formly.validation.minLength', {
            value: field.requiredLength,
          });
        },
      },
      {
        name: 'maxLength',
        message(error: any) {
          return `Lunghezza massima consentita: ${error.requiredLength}.`;
        },
      },
      {
        name: 'min',
        message(error: any) {
          return `Il valore minimo consentito è ${error.min}.`;
        },
      },
      {
        name: 'max',
        message(error: any) {
          return `Il valore massimo consentito è ${error.max}.`;
        },
      },
      {
        name: 'maxFileSize',
        message(error: any) {
          return translate.stream('formly.validation.maxFileSize', {
            maxFileSize: error,
          });
        },
      },
    ],
  };
}
